import { MONS_ROUTES } from 'src/constants/route';
import { ClientLog } from 'src/constants/clientLog';
import ClientLogger from 'src/utils/logger';

/**
 * Builds the Mons sign-in URL with redirect_to pointing back to the current page.
 * Uses `use_default_idp` to skip IDP selection and go straight to AuthPortal.
 */
export const buildSignInRedirectUrl = (): string => {
  const currentPath = window.location.pathname + window.location.search;
  return `${MONS_ROUTES.SIGN_IN}?redirect_to=${encodeURIComponent(currentPath)}&use_default_idp`;
};

// Module-level guard: multiple concurrent API calls can all return 412 before the
// browser navigates away. This ensures only the first one triggers the redirect and logs.
let isRedirecting = false;

interface RedirectLogContext {
  message: string;
  url: string;
  endpoint: string;
}

/**
 * Redirect to Mons sign-in page for re-authentication.
 * Used when SUOS returns 412 (PreconditionFailed) indicating a gated user's
 * auth has expired. After sign-in, Mons redirects back to the same page.
 *
 * No-op if a redirect is already in progress (guards against concurrent 412s
 * producing duplicate logs and redundant navigations).
 */
export const redirectToSignIn = (logContext: RedirectLogContext): void => {
  if (isRedirecting) return;
  isRedirecting = true;
  ClientLogger.info(ClientLog.AuthenticationExpiredRedirect, logContext);
  window.location.href = buildSignInRedirectUrl();
};

/** Exposed for testing only — resets the redirect guard between test cases. */
export const resetRedirectGuard = (): void => {
  isRedirecting = false;
};
